/**
 * guestsV2Demo — el modelo de HUÉSPED de la vista previa V2.
 *
 * Lo que este módulo defiende (PRODUCT_V2_SPEC §7.1):
 *
 *   Reserva ≠ Huésped ≠ Unidad física.
 *
 * Una reserva es un contrato sobre un tipo de alojamiento. Un huésped es una
 * persona, y una reserva puede llevar varias. Hasta ahora la demo guardaba la
 * preparación del huésped como una fila plana por reserva
 * (`todayV2Demo.GUEST_READINESS`), que servía para pintar Hoy pero no era un
 * modelo: no había personas, ni acompañantes con nombre, ni datos por persona.
 * Aquí están las personas.
 *
 * Lo que NO hay aquí, a propósito:
 *   - contenido de documentos. Ni un número de pasaporte, ni una imagen. Solo
 *     el ESTADO del documento (§7.3.5): el contenido vive en su propio ámbito
 *     `guest_document` y no lo toca ninguna pantalla operativa.
 *   - contactos inventados. Si un huésped no trae email o teléfono, el campo
 *     queda vacío y la pantalla dice que no está.
 *   - religión, ni nada que se le parezca. La necesidad operativa es si el
 *     minibar va sin alcohol; preguntar por la religión de alguien para
 *     deducirlo sería perfilado, y además daría peor dato.
 *
 * Sin red, sin backend, sin persistencia.
 */

// ---------------------------------------------------------------------------
// Estados (los nombres aprobados en PRODUCT_V2_SPEC §7.4)
// ---------------------------------------------------------------------------

/**
 * Pre-check-in: `not_started → in_progress → completed → verified`.
 *
 * No es un interruptor que alguien marca: se DERIVA de los datos que la
 * propiedad marcó como obligatorios en Ajustes (`guestsV2Selectors`).
 * `verified` es el único que pone una persona, cuando recepción comprueba el
 * documento contra la persona que tiene delante.
 */
export const PRE_CHECKIN_FLOW = ["not_started", "in_progress", "completed", "verified"];

export const PRE_CHECKIN_LABEL = {
  not_started: "Sin empezar",
  in_progress: "A medias",
  completed: "Completado",
  verified: "Verificado",
};

export const PRE_CHECKIN_TONE = {
  not_started: "stone",
  in_progress: "amber",
  completed: "emerald",
  verified: "emerald",
};

/**
 * Reporte de extranjeros (APOA en Indonesia): `due → filed`, o `not_required`
 * si la propiedad no lo tiene activado en Ajustes.
 *
 * Es un proceso PROPIO y de la propiedad, no del huésped. No se deduce de nada:
 * ni de que el pre-check-in esté completo, ni de que el huésped ya haya
 * entrado, ni de que se haya marchado. Solo un dato explícito lo marca
 * presentado. El producto prepara la cola y exporta; presenta la propiedad. No
 * hay integración con ningún sistema gubernamental, ni la habrá en estas fases
 * (§7.4).
 */
export const IMMIGRATION_FLOW = ["not_required", "due", "filed"];

export const IMMIGRATION_LABEL = {
  due: "Pendiente de presentar",
  filed: "Presentado",
  not_required: "No aplica",
};

/**
 * Estado del documento de identidad. SOLO estado.
 *
 *   `not_provided`  no consta nada.
 *   `declared`      el huésped CONFIRMA que trae el documento. No hay archivo.
 *   `provided`      existe un archivo aportado por el huésped.
 *   `verified`      recepción lo comprobó contra la persona que tenía delante.
 *
 * LÍMITE DE ESTA VISTA PREVIA: `provided` es INALCANZABLE. Aquí no hay subida
 * de ficheros, así que nadie puede haber aportado un archivo y ninguna pantalla
 * debe insinuar que existe uno. El pre-check-in del huésped llega como mucho a
 * `declared`; a `verified` se llega en recepción, en persona. El estado se
 * conserva en el vocabulario porque es el modelo al que se va cuando exista el
 * ámbito `guest_document` (§7.3.5), no porque se use hoy.
 */
export const DOCUMENT_STATUS = ["not_provided", "declared", "provided", "verified"];

/**
 * Etiquetas para el equipo. `declared` dice en voz alta que NO hay archivo: una
 * casilla marcada no puede leerse en recepción como un pasaporte subido.
 */
export const DOCUMENT_STATUS_LABEL = {
  not_provided: "Sin confirmar",
  declared: "Confirmado por el huésped, sin archivo",
  provided: "Archivo aportado",
  verified: "Comprobado en recepción",
};

// ---------------------------------------------------------------------------
// Llegada
// ---------------------------------------------------------------------------

/**
 * Cómo llega el huésped. Los campos que se piden después dependen de esto: un
 * número de vuelo no tiene sentido si viene en barco.
 *
 * `transfer_label` dice de dónde sale el traslado que la propiedad sabe
 * organizar. Quien llega por su cuenta NO tiene punto de recogida, así que no
 * se le pregunta: el catálogo de Ajustes (§9) solo tiene traslados desde el
 * aeropuerto y desde el puerto, y no hay ningún traslado genérico aprobado.
 * Enseñarle "Traslado desde el aeropuerto" a quien viene en su propio coche es
 * ofrecer un servicio que no existe.
 */
export const ARRIVAL_MODES = [
  {
    value: "plane", label: "En avión",
    ref_label: "Número de vuelo", ref_placeholder: "GA-256",
    time_label: "Hora de aterrizaje",
    transfer_label: "Traslado desde el aeropuerto",
  },
  {
    value: "boat", label: "En barco",
    ref_label: "Barco o puerto", ref_placeholder: "Fast boat · Padangbai",
    time_label: "Hora de llegada al puerto",
    transfer_label: "Traslado desde el puerto",
  },
  {
    value: "other", label: "Por mi cuenta",
    ref_label: null, ref_placeholder: null,
    time_label: null,
    transfer_label: null,
  },
];

/** El modo de llegada, con sus campos. Nunca `undefined`. */
export function arrivalModeOf(value) {
  return ARRIVAL_MODES.find((m) => m.value === value)
    || ARRIVAL_MODES[ARRIVAL_MODES.length - 1];
}

export const ARRIVAL_MODE_LABEL = Object.fromEntries(
  ARRIVAL_MODES.map((m) => [m.value, m.label]),
);

/**
 * Traslado. Reservarlo crea un pedido de servicio real del catálogo de Ajustes
 * (§9); "me lo organizo yo" es una respuesta legítima y se guarda, para que
 * recepción no persiga a quien ya lo tiene resuelto.
 */
export const TRANSFER_CHOICES = [
  { value: "book", label: "Reservar traslado" },
  { value: "own", label: "Me lo organizo yo" },
];

export const TRANSFER_STATUS_LABEL = {
  book: "Traslado solicitado",
  own: "Traslado por su cuenta",
  none: "Traslado sin indicar",
  // A quien llega por su cuenta no se le pregunta: no hay nada pendiente que
  // recepción tenga que perseguir, así que tampoco se dice "sin indicar".
  not_asked: null,
};

// ---------------------------------------------------------------------------
// Preferencias
// ---------------------------------------------------------------------------

/**
 * Minibar. La necesidad operativa es UNA: si la villa se prepara con alcohol o
 * sin alcohol. Eso se pregunta directamente.
 *
 * NO se pregunta por la religión de nadie. Sería un dato sensible recogido
 * para una decisión que no lo necesita, y además peor: hay quien no bebe por
 * salud, por embarazo, por deporte o porque viaja con niños, y hay creyentes a
 * quienes les da igual lo que haya en la nevera. La pregunta correcta es la
 * que describe el resultado que el huésped quiere.
 */
export const MINIBAR_OPTIONS = [
  { value: "standard", label: "Estándar" },
  { value: "alcohol_free", label: "Sin alcohol" },
];

export const MINIBAR_LABEL = Object.fromEntries(
  MINIBAR_OPTIONS.map((o) => [o.value, o.label]),
);

// ---------------------------------------------------------------------------
// Campos del pre-check-in
// ---------------------------------------------------------------------------

/**
 * Campos que el formulario sabe pedir, y a quién.
 *
 *   `scope: "party"`   se pide una vez por reserva (contacto, llegada).
 *   `scope: "person"`  se pide por persona (nombre, nacionalidad, documento).
 *
 * Qué es OBLIGATORIO no se decide aquí: sale de Ajustes › Requisitos del
 * huésped. Este módulo solo sabe qué campos existen y cómo se piden; la
 * propiedad decide cuáles necesita. Dolce es configuración, no el producto.
 *
 * `step` es el paso del formulario donde se rellena cada campo. Sirve para que,
 * si falta algo al enviar, se pueda llevar al huésped exactamente a donde se
 * arregla, en vez de dejarle delante de un error sin sitio al que ir.
 */
export const GUEST_FIELDS = {
  full_name: { scope: "person", kind: "text", step: "guests" },
  nationality: { scope: "person", kind: "text", step: "guests" },
  date_of_birth: { scope: "person", kind: "date", step: "guests" },
  passport: { scope: "person", kind: "document", step: "guests" },
  email: { scope: "party", kind: "email", step: "guests" },
  phone: { scope: "party", kind: "tel", step: "guests" },
  arrival_time: { scope: "party", kind: "time", step: "arrival" },
  flight_number: { scope: "party", kind: "text", step: "arrival" },
};

/**
 * Preferencias y extras: SIEMPRE opcionales. No bloquean nunca el
 * pre-check-in, por muy útiles que sean para operaciones. Un huésped sin
 * alergias no tiene un pre-check-in incompleto.
 */
export const OPTIONAL_FIELDS = [
  "allergies", "dietary", "minibar", "scooter_interest", "notes",
  "transfer", "arrival_mode", "arrival_reference", "estimated_arrival",
];

export const PRE_CHECKIN_STEPS = [
  { key: "guests", label: "Huéspedes" },
  { key: "arrival", label: "Llegada" },
  { key: "preferences", label: "Preferencias" },
  { key: "review", label: "Confirmar" },
];

// ---------------------------------------------------------------------------
// Enlace del huésped — SOLO DEMOSTRACIÓN
// ---------------------------------------------------------------------------

/**
 * LÍMITE DE LA VISTA PREVIA. En el producto real, el enlace del huésped es un
 * token de ≥128 bits, guardado HASHEADO, atado a una reserva, con caducidad y
 * revocación, y la URL no contiene ni el id de la reserva ni el nombre de
 * nadie (§7.3.1). Nada de eso existe todavía: no hay backend de huésped.
 *
 * Estos tokens son etiquetas legibles de demostración, con un prefijo que lo
 * dice. No son secretos, no autentican nada y no protegen nada: la vista
 * previa no tiene datos reales que proteger. Cuando exista el token real, lo
 * único que cambia es de dónde sale `reservation_id`.
 */
export const DEMO_TOKEN_PREFIX = "demo-";

export const DEMO_GUEST_LINKS = {
  "demo-vandijk": "R2028006",
  // Sin ficha de persona: el titular sale de la propia reserva y no consta ni
  // su email ni su teléfono. El portal lo dice; no se inventa un contacto.
  "demo-weber": "R2028004",
  "demo-martin": "R2028005",
  "demo-moreno": "R2028009",
  "demo-fischer": "R2028015",
  "demo-papadakis": "R2028024",
  "demo-menon": "R2028022",
  "demo-almeida": "R2028031",
  "demo-lindqvist": "R2028003",
};

/**
 * El aviso de vista previa lo da ahora el armazón del portal
 * (`guestPortalV2Demo.PORTAL_DEMO_NOTE`), que es donde se ve en todas las
 * pantallas del huésped y no solo en el pre-check-in. Un solo aviso, un solo
 * sitio.
 */

// ---------------------------------------------------------------------------
// Personas
// ---------------------------------------------------------------------------

/**
 * Huéspedes declarados, reutilizando las personas que ya existen en las
 * reservas de la demo. Una fila por PERSONA, con su `reservation_id`.
 *
 * ESTA ES LA ÚNICA FUENTE del contacto del huésped. Antes el mismo email vivía
 * también en `RESERVATION_EXTRAS[id].guest`, y dos copias de un dato son dos
 * datos que acaban distintos: se corrige una, se olvida la otra, y la ficha de
 * la reserva y la pantalla de Huéspedes enseñan teléfonos diferentes de la
 * misma persona. El contacto es de la PERSONA, no del contrato, así que vive
 * aquí; `guestContactFor()` lo deriva de este modelo para lo que todavía lo
 * consume por compatibilidad (búsqueda de reservas). Lo mismo con los
 * acompañantes: aquí hay personas con `guest_id`, no una lista de nombres.
 *
 * Notas sobre lo que falta a propósito:
 *   - `email`/`phone` solo donde la reserva ya los declaraba. Donde no, van a
 *     `null` y la pantalla dice "No disponible". Inventarlos sería fabricar
 *     datos operativos.
 *   - `document`: solo estado. Jamás número ni imagen.
 *   - `date_of_birth` solo donde hace falta para inmigración y consta.
 */
export const GUESTS = [
  // --- R2028006 · Emma van Dijk · V02 — pre-check-in A MEDIAS -------------
  // Falta documento y hora de llegada: es el caso "2 datos pendientes".
  {
    guest_id: "G-2028-006-1",
    reservation_id: "R2028006",
    primary_guest: true,
    full_name: "Emma van Dijk",
    nationality: "Países Bajos",
    date_of_birth: "1989-04-11",
    document: { status: "not_provided", type: null },
    email: "emma.vandijk@ejemplo.demo",
    phone: "+31 6 1234 0000",
  },
  {
    guest_id: "G-2028-006-2",
    reservation_id: "R2028006",
    primary_guest: false,
    full_name: "Tom van Dijk",
    nationality: "Países Bajos",
    date_of_birth: null,
    document: { status: "not_provided", type: null },
    email: null,
    phone: null,
  },

  // --- R2028009 · Carla Moreno · V03 — COMPLETADO, APOA PENDIENTE ---------
  // Tres personas, alergia declarada. Demuestra que un pre-check-in completo
  // NO implica que el reporte de inmigración esté presentado.
  {
    guest_id: "G-2028-009-1",
    reservation_id: "R2028009",
    primary_guest: true,
    full_name: "Carla Moreno",
    nationality: "España",
    date_of_birth: "1985-09-02",
    document: { status: "verified", type: "passport" },
    email: "carla.moreno@ejemplo.demo",
    phone: "+34 600 000 000",
  },
  {
    guest_id: "G-2028-009-2",
    reservation_id: "R2028009",
    primary_guest: false,
    full_name: "Marc Soler",
    nationality: "España",
    date_of_birth: "1983-01-24",
    document: { status: "verified", type: "passport" },
    email: null,
    phone: null,
  },
  {
    guest_id: "G-2028-009-3",
    reservation_id: "R2028009",
    primary_guest: false,
    full_name: "Nil Soler",
    nationality: "España",
    date_of_birth: "2019-06-30",
    document: { status: "declared", type: "passport" },
    email: null,
    phone: null,
  },

  // --- R2028015 · Familia Fischer · V05 — LISTA PARA LA LLEGADA ----------
  // Completado y reporte ya presentado: la única fila que sale "lista".
  {
    guest_id: "G-2028-015-1",
    reservation_id: "R2028015",
    primary_guest: true,
    full_name: "Anna Fischer",
    nationality: "Suiza",
    date_of_birth: "1981-03-17",
    document: { status: "verified", type: "passport" },
    email: "anna.fischer@ejemplo.demo",
    phone: "+41 79 000 00 00",
  },
  {
    guest_id: "G-2028-015-2",
    reservation_id: "R2028015",
    primary_guest: false,
    full_name: "Lukas Fischer",
    nationality: "Suiza",
    date_of_birth: "1979-11-05",
    document: { status: "verified", type: "passport" },
    email: null,
    phone: null,
  },
  {
    guest_id: "G-2028-015-3",
    reservation_id: "R2028015",
    primary_guest: false,
    full_name: "Mia Fischer",
    nationality: "Suiza",
    date_of_birth: "2015-02-08",
    document: { status: "verified", type: "passport" },
    email: null, phone: null,
  },
  {
    guest_id: "G-2028-015-4",
    reservation_id: "R2028015",
    primary_guest: false,
    full_name: "Noah Fischer",
    nationality: "Suiza",
    date_of_birth: "2017-07-21",
    document: { status: "verified", type: "passport" },
    email: null, phone: null,
  },
  {
    guest_id: "G-2028-015-5",
    reservation_id: "R2028015",
    primary_guest: false,
    full_name: "Elin Fischer",
    nationality: "Suiza",
    date_of_birth: "2021-10-02",
    document: { status: "verified", type: "passport" },
    email: null, phone: null,
  },

  // --- R2028024 · Familia Papadakis · V08 — VUELO + TRASLADO -------------
  {
    guest_id: "G-2028-024-1",
    reservation_id: "R2028024",
    primary_guest: true,
    full_name: "Nikos Papadakis",
    nationality: "Grecia",
    date_of_birth: "1978-05-14",
    document: { status: "declared", type: "passport" },
    email: "papadakis@ejemplo.demo",
    phone: "+30 69 0000 0000",
  },
  {
    guest_id: "G-2028-024-2",
    reservation_id: "R2028024",
    primary_guest: false,
    full_name: "Sofia Papadaki",
    nationality: "Grecia",
    date_of_birth: "1980-08-03",
    document: { status: "declared", type: "passport" },
    email: null, phone: null,
  },
  {
    guest_id: "G-2028-024-3",
    reservation_id: "R2028024",
    primary_guest: false,
    full_name: "Eleni Papadaki",
    nationality: "Grecia",
    date_of_birth: "2013-12-19",
    document: { status: "declared", type: "passport" },
    email: null, phone: null,
  },

  // --- R2028022 · Ravi Menon · V07 — MINIBAR SIN ALCOHOL ------------------
  // Sin empezar el pre-check-in: la reserva está sin confirmar y sin cobrar.
  {
    guest_id: "G-2028-022-1",
    reservation_id: "R2028022",
    primary_guest: true,
    full_name: "Ravi Menon",
    nationality: "Emiratos Árabes Unidos",
    date_of_birth: null,
    document: { status: "not_provided", type: null },
    email: "ravi.menon@ejemplo.demo",
    phone: "+971 50 000 0000",
  },

  // --- R2028003 · Sara Lindqvist · V01 — YA ALOJADA, APOA PENDIENTE -------
  // El caso que prueba que entrar NO presenta el reporte de inmigración.
  {
    guest_id: "G-2028-003-1",
    reservation_id: "R2028003",
    primary_guest: true,
    full_name: "Sara Lindqvist",
    nationality: "Suecia",
    date_of_birth: "1990-06-12",
    document: { status: "verified", type: "passport" },
    email: "sara.lindqvist@ejemplo.demo",
    phone: "+46 70 000 00 00",
  },
  {
    guest_id: "G-2028-003-2",
    reservation_id: "R2028003",
    primary_guest: false,
    full_name: "Johan Lindqvist",
    nationality: "Suecia",
    date_of_birth: "1988-02-27",
    document: { status: "verified", type: "passport" },
    email: null, phone: null,
  },
  {
    guest_id: "G-2028-003-3",
    reservation_id: "R2028003",
    primary_guest: false,
    full_name: "Elsa Lindqvist",
    nationality: "Suecia",
    date_of_birth: "2018-04-09",
    document: { status: "verified", type: "passport" },
    email: null, phone: null,
  },

  // --- R2028031 · Lucas Almeida · V10 — SIN EMPEZAR -----------------------
  {
    guest_id: "G-2028-031-1",
    reservation_id: "R2028031",
    primary_guest: true,
    full_name: "Lucas Almeida",
    nationality: "Brasil",
    date_of_birth: null,
    document: { status: "not_provided", type: null },
    email: "lucas.almeida@ejemplo.demo",
    phone: "+55 11 90000 0000",
  },

  // --- R2028029c · James O'Connor · V10 — CANCELADA -----------------------
  // Una reserva cancelada conserva a su gente: se anula el contrato, no se
  // borra a quien lo firmó. Su contacto estaba declarado en la reserva y se
  // muda aquí tal cual; no se inventa ni se recompone nada.
  {
    guest_id: "G-2028-029-1",
    reservation_id: "R2028029c",
    primary_guest: true,
    full_name: "James O'Connor",
    nationality: "Irlanda",
    date_of_birth: null,
    document: { status: "not_provided", type: null },
    email: "james.oconnor@ejemplo.demo",
    phone: "+353 86 000 0000",
  },
];

// ---------------------------------------------------------------------------
// Lo que el huésped ya envió (semilla del pre-check-in)
// ---------------------------------------------------------------------------

/**
 * Respuestas de pre-check-in por reserva. Es lo que el huésped mandó desde su
 * enlace; en la vista previa se declara aquí y lo que se envíe en sesión se
 * apila encima (`guestsV2Store`).
 *
 * `immigration` NO está en este objeto a propósito: es de la propiedad, no del
 * huésped, y vive aparte (`IMMIGRATION_CASES`) para que no haya forma de que
 * completar el formulario lo mueva.
 */
export const PRE_CHECKIN_SUBMISSIONS = {
  R2028006: {
    submitted_at: "2028-08-18T09:22:00Z",
    arrival: {
      mode: "plane", reference: "KL-809", landing_time: null,
      transfer: "own", estimated_arrival: null,
    },
    preferences: { allergies: "", dietary: "", minibar: "standard", scooter_interest: false, notes: "" },
  },
  R2028009: {
    submitted_at: "2028-08-19T17:40:00Z",
    arrival: {
      mode: "plane", reference: "IB-6845", landing_time: "12:20",
      transfer: "own", estimated_arrival: "14:00",
    },
    preferences: {
      allergies: "Nil (5 años): alergia a los frutos secos.",
      dietary: "Sin cerdo.",
      minibar: "standard",
      scooter_interest: true,
      notes: "Repiten estancia. Pidieron la V03 expresamente.",
    },
  },
  R2028015: {
    submitted_at: "2028-08-16T08:05:00Z",
    arrival: {
      mode: "plane", reference: "SQ-942", landing_time: "10:15",
      transfer: "book", estimated_arrival: "13:30",
    },
    preferences: {
      allergies: "", dietary: "Una cuna para Elin.", minibar: "standard",
      scooter_interest: false, notes: "",
    },
  },
  R2028024: {
    submitted_at: "2028-08-20T18:25:00Z",
    arrival: {
      mode: "plane", reference: "GA-256", landing_time: "21:40",
      transfer: "book", estimated_arrival: "23:10",
    },
    preferences: {
      allergies: "", dietary: "", minibar: "standard",
      scooter_interest: false,
      notes: "Llegada nocturna con tres niños; agradecerían cena ligera.",
    },
  },
  R2028003: {
    submitted_at: "2028-08-17T11:02:00Z",
    arrival: {
      mode: "plane", reference: "TK-68", landing_time: "09:05",
      transfer: "own", estimated_arrival: "15:00",
    },
    preferences: {
      allergies: "Elsa: intolerancia a la lactosa.", dietary: "",
      minibar: "standard", scooter_interest: true, notes: "",
    },
  },
  R2028022: {
    submitted_at: "2028-08-16T09:10:00Z",
    // Llega por su cuenta: sin vuelo, sin hora de aterrizaje y SIN pregunta de
    // traslado. `transfer: null` no es un dato que falte, es una pregunta que
    // no se hace.
    arrival: {
      mode: "other", reference: null, landing_time: null,
      transfer: null, estimated_arrival: null,
    },
    // Sin alcohol porque lo pidió así. No se pregunta el porqué.
    preferences: {
      allergies: "", dietary: "", minibar: "alcohol_free",
      scooter_interest: false, notes: "",
    },
  },
};

// ---------------------------------------------------------------------------
// Inmigración — proceso de la propiedad
// ---------------------------------------------------------------------------

/**
 * Estado del reporte de extranjeros POR RESERVA, declarado.
 *
 * Vive separado de las respuestas del huésped a propósito: es trabajo de la
 * propiedad. Lo que no esté declarado aquí, con el reporte activado en
 * Ajustes, queda `due` — no porque se deduzca nada, sino porque no consta que
 * se haya presentado. Nunca `filed` por inferencia.
 */
export const IMMIGRATION_CASES = {
  R2028015: { state: "filed", filed_at: "2028-08-20T09:30:00Z", actor: "Ana Prasetya" },
  R2028009: { state: "due", filed_at: null, actor: null },
  R2028006: { state: "due", filed_at: null, actor: null },
  R2028024: { state: "due", filed_at: null, actor: null },
  // Ya está alojada y el reporte sigue pendiente: entrar no lo presenta.
  R2028003: { state: "due", filed_at: null, actor: null },
};
